
import { useState, useCallback, useEffect } from 'react';
import { DatabaseMaterial } from './useMaterials';

export type MaterialSortField =
  | 'code'
  | 'name'
  | 'category'
  | 'supplier'
  | 'unit_price'
  | 'discount'
  | 'family_discount_pct'
  | 'incidence_per_sqm'
  | 'installation_time_per_sqm'
  | 'cost_per_sqm';

export type SortDirection = 'asc' | 'desc';

const STORAGE_KEY = 'materials-sort';

interface PersistedSort {
  field: MaterialSortField;
  direction: SortDirection;
}

function readPersistedSort(): PersistedSort | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.field && parsed?.direction) return parsed as PersistedSort;
  } catch {
    // ignore
  }
  return null;
}

function writePersistedSort(field: MaterialSortField, direction: SortDirection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ field, direction }));
  } catch {
    // ignore
  }
}

export interface UseMaterialsSortingReturn {
  sortField: MaterialSortField;
  sortDirection: SortDirection;
  setSort: (field: MaterialSortField) => void;
  getSortedMaterials: (materials: DatabaseMaterial[]) => DatabaseMaterial[];
}

// Calcolo di costo/m²
const getCostPerSqm = (material: DatabaseMaterial) => {
  const incidence = material.incidence_per_sqm || 1;
  // Usa calculateFinalPrice per gestire sconti cumulativi
  const { calculateFinalPrice } = require('@/utils/discountUtils');
  const finalPrice = calculateFinalPrice(material.unit_price, material.list_price, material.discount);
  return finalPrice * incidence;
};

export function useMaterialsSorting(
  initialField: MaterialSortField = 'name',
  initialDirection: SortDirection = 'asc'
): UseMaterialsSortingReturn {
  // Lazy init: leggi una sola volta da localStorage al mount per ripristinare
  // l'ultima ordinamento scelta dall'utente. Fallback ai default props.
  const [sortField, setSortField] = useState<MaterialSortField>(() => {
    const persisted = readPersistedSort();
    return persisted?.field ?? initialField;
  });
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    const persisted = readPersistedSort();
    return persisted?.direction ?? initialDirection;
  });

  // Persist ogni cambio per averlo già pronto alla prossima apertura.
  useEffect(() => {
    writePersistedSort(sortField, sortDirection);
  }, [sortField, sortDirection]);

  const setSort = useCallback((field: MaterialSortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const getSortedMaterials = useCallback(
    (materials: DatabaseMaterial[]) => {
      const sorted = [...materials].sort((a, b) => {
        let av: any, bv: any;

        switch (sortField) {
          case 'code':
          case 'name':
          case 'supplier':
          case 'category':
            av = (a[sortField] || '').toLowerCase();
            bv = (b[sortField] || '').toLowerCase();
            break;
          case 'unit_price':
          case 'discount':
          case 'incidence_per_sqm':
          case 'installation_time_per_sqm':
            // NULL/0 trattati come 0 → in ordine crescente i materiali senza
            // tempo di posa finiscono in cima (facile individuare i buchi).
            av = a[sortField] ?? 0;
            bv = b[sortField] ?? 0;
            break;
          case 'family_discount_pct':
            // Materiali senza famiglia in fondo (asc) → uso -1 come sentinella
            av = (a as { family_id?: string | null }).family_id ? (a as any).family_discount_pct ?? 0 : -1;
            bv = (b as { family_id?: string | null }).family_id ? (b as any).family_discount_pct ?? 0 : -1;
            break;
          case 'cost_per_sqm':
            av = getCostPerSqm(a);
            bv = getCostPerSqm(b);
            break;
          default:
            av = a[sortField];
            bv = b[sortField];
        }

        if (typeof av === 'number' && typeof bv === 'number') {
          return av - bv;
        }
        if (av < bv) return -1;
        if (av > bv) return 1;
        return 0;
      });

      if (sortDirection === 'desc') sorted.reverse();
      return sorted;
    },
    [sortField, sortDirection]
  );

  return {
    sortField,
    sortDirection,
    setSort,
    getSortedMaterials,
  };
}
